import { FormEvent, useEffect, useState } from "react";
import { kpsiGet, kpsiPost } from "./kpsiApi";

type WorkRow = { position?: string; startDate?: string; endDate?: string; reason?: string };

type FormPayload = {
  test: Record<string, unknown> & {
    id: number;
    companyId: number;
    firstName: string;
    lastName: string;
    position: string;
    dateFaxReceived: string;
    stage: number;
    score: string;
  };
  work: WorkRow[];
  companyName: string;
  canEditScore: boolean;
  stageLocked: boolean;
  stageOptions: Array<{ value: number; name: string }>;
};

interface Props {
  id?: number;
  companyId?: number;
  onBack: () => void;
  onSaved: (id: number) => void;
}

const TABS = ["Basic Info", "KPSI Info", "Color Info", "Education", "Previous Work", "Notes"] as const;

function isWorkEmpty(row: WorkRow) {
  return !(row.position || row.startDate || row.endDate || row.reason);
}

/** Match legacy Edit: always end with one blank row; never pad to three empties. */
function withTrailingBlank(rows: WorkRow[] | null | undefined): WorkRow[] {
  const list = rows?.length ? [...rows] : [];
  if (list.length === 0 || !isWorkEmpty(list[list.length - 1]!)) {
    list.push({});
  }
  return list;
}

export default function KpsiTestEdit({ id, companyId, onBack, onSaved }: Props) {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState<FormPayload | null>(null);
  const [work, setWork] = useState<WorkRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const url = id
      ? `/Kpsi/Tests/${id}`
      : `/Kpsi/Tests/New?companyId=${companyId ?? 0}`;
    void kpsiGet<FormPayload>(url)
      .then((data) => {
        setForm(data);
        setWork(withTrailingBlank(data.work));
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Load failed"));
  }, [id, companyId]);

  if (error && !form) return <p className="kpsi-alert">{error}</p>;
  if (!form) return <p className="kpsi-muted">Loading…</p>;

  const t = form.test;
  const isEdit = !!t.id;

  function setField(key: string, value: string | number) {
    setForm((prev) => (prev ? { ...prev, test: { ...prev.test, [key]: value } } : prev));
  }

  function goNext() {
    setTab((cur) => Math.min(cur + 1, TABS.length - 1));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const body = {
        id: t.id,
        companyId: t.companyId,
        firstName: t.firstName,
        lastName: t.lastName,
        position: t.position,
        dateFaxReceived: t.dateFaxReceived,
        stage: Number(t.stage),
        score: t.score,
        education: t.education,
        adj: t.adj,
        range: t.range,
        des1: t.des1,
        des2: t.des2,
        des3: t.des3,
        des4: t.des4,
        sort: t.sort,
        word1: t.word1,
        word2: t.word2,
        word3: t.word3,
        word4: t.word4,
        eng: t.eng,
        green: t.green,
        red: t.red,
        blue: t.blue,
        yellow: t.yellow,
        notes: t.notes,
        work,
      };
      const result = await kpsiPost<{ id: number }>("/Kpsi/Tests", body);
      onSaved(result.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  return (
    <>
      <p>
        <button type="button" className="kpsi-link-btn" onClick={onBack}>
          &laquo; {isEdit ? "Back to record" : "Back to list"}
        </button>
      </p>
      <h1>
        {isEdit ? "Edit Record" : "Add New Record"}{" "}
        <small>{form.companyName}</small>
      </h1>
      <p className="kpsi-muted">
        Required items are marked with a <span className="kpsi-required">*</span>.
      </p>
      {error && <div className="kpsi-alert">{error}</div>}

      <form onSubmit={(e) => void onSubmit(e)}>
        <div className="kpsi-tabs-bar">
          <div className="kpsi-tabs">
            {TABS.map((name, i) => (
              <button
                key={name}
                type="button"
                className={tab === i ? "active" : ""}
                onClick={() => setTab(i)}
              >
                {name}
              </button>
            ))}
          </div>
          <button type="submit" className="kpsi-btn-primary">
            Submit &raquo;
          </button>
        </div>

        <div className="kpsi-tab-pane">
          {tab === 0 && (
            <>
              <div className="kpsi-form-grid kpsi-form-grid-basic">
                <div className="kpsi-field">
                  <label>
                    First Name <span className="kpsi-required">*</span>
                  </label>
                  <input
                    value={String(t.firstName ?? "")}
                    onChange={(e) => setField("firstName", e.target.value)}
                    required
                  />
                </div>
                <div className="kpsi-field">
                  <label>
                    Last Name <span className="kpsi-required">*</span>
                  </label>
                  <input
                    value={String(t.lastName ?? "")}
                    onChange={(e) => setField("lastName", e.target.value)}
                    required
                  />
                </div>
                <div className="kpsi-field">
                  <label>
                    Position Sought <span className="kpsi-required">*</span>
                  </label>
                  <input
                    value={String(t.position ?? "")}
                    onChange={(e) => setField("position", e.target.value)}
                    required
                  />
                </div>
                <div className="kpsi-field">
                  <label>
                    Date Fax Received <span className="kpsi-required">*</span>
                  </label>
                  <input
                    type="date"
                    value={String(t.dateFaxReceived ?? "")}
                    onChange={(e) => setField("dateFaxReceived", e.target.value)}
                    required
                  />
                </div>
                <div className="kpsi-field">
                  <label>
                    Stage <span className="kpsi-required">*</span>
                  </label>
                  <select
                    value={Number(t.stage)}
                    disabled={form.stageLocked}
                    onChange={(e) => setField("stage", Number(e.target.value))}
                  >
                    {form.stageOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="kpsi-field">
                  <label>Score</label>
                  <input
                    value={String(t.score ?? "")}
                    disabled={!form.canEditScore}
                    onChange={(e) => setField("score", e.target.value)}
                  />
                  {!form.canEditScore && (
                    <div className="kpsi-muted">Score will be entered by the interpreter.</div>
                  )}
                </div>
              </div>
              <button type="button" className="kpsi-btn-secondary" onClick={goNext}>
                Next
              </button>
            </>
          )}

          {tab === 1 && (
            <>
              <div className="kpsi-form-grid kpsi-form-grid-kpsi">
                <div className="kpsi-field">
                  <label>ADJ</label>
                  <textarea value={String(t.adj ?? "")} onChange={(e) => setField("adj", e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label>Range</label>
                  <textarea value={String(t.range ?? "")} onChange={(e) => setField("range", e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label>Sort</label>
                  <textarea value={String(t.sort ?? "")} onChange={(e) => setField("sort", e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label>ENG</label>
                  <textarea value={String(t.eng ?? "")} onChange={(e) => setField("eng", e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label>DES</label>
                  <div className="kpsi-color-grid">
                    <span>G=</span>
                    <input value={String(t.des1 ?? "")} onChange={(e) => setField("des1", e.target.value)} />
                    <span>R=</span>
                    <input value={String(t.des2 ?? "")} onChange={(e) => setField("des2", e.target.value)} />
                    <span>B=</span>
                    <input value={String(t.des3 ?? "")} onChange={(e) => setField("des3", e.target.value)} />
                    <span>Y=</span>
                    <input value={String(t.des4 ?? "")} onChange={(e) => setField("des4", e.target.value)} />
                  </div>
                </div>
                <div className="kpsi-field">
                  <label>Word</label>
                  <div className="kpsi-color-grid">
                    <span>G=</span>
                    <input value={String(t.word1 ?? "")} onChange={(e) => setField("word1", e.target.value)} />
                    <span>R=</span>
                    <input value={String(t.word2 ?? "")} onChange={(e) => setField("word2", e.target.value)} />
                    <span>B=</span>
                    <input value={String(t.word3 ?? "")} onChange={(e) => setField("word3", e.target.value)} />
                    <span>Y=</span>
                    <input value={String(t.word4 ?? "")} onChange={(e) => setField("word4", e.target.value)} />
                  </div>
                </div>
              </div>
              <button type="button" className="kpsi-btn-secondary" onClick={goNext}>
                Next
              </button>
            </>
          )}

          {tab === 2 && (
            <>
              <div className="kpsi-form-grid kpsi-form-grid-colors">
                <div className="kpsi-field">
                  <label className="green">Green</label>
                  <textarea value={String(t.green ?? "")} onChange={(e) => setField("green", e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label className="red">Red</label>
                  <textarea value={String(t.red ?? "")} onChange={(e) => setField("red", e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label className="blue">Blue</label>
                  <textarea value={String(t.blue ?? "")} onChange={(e) => setField("blue", e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label className="yellow">Yellow</label>
                  <textarea value={String(t.yellow ?? "")} onChange={(e) => setField("yellow", e.target.value)} />
                </div>
              </div>
              <button type="button" className="kpsi-btn-secondary" onClick={goNext}>
                Next
              </button>
            </>
          )}

          {tab === 3 && (
            <>
              <div className="kpsi-field">
                <label>Education/Skills</label>
                <textarea
                  value={String(t.education ?? "")}
                  onChange={(e) => setField("education", e.target.value)}
                  rows={8}
                />
              </div>
              <button type="button" className="kpsi-btn-secondary" onClick={goNext}>
                Next
              </button>
            </>
          )}

          {tab === 4 && (
            <>
              <p>
                <button
                  type="button"
                  className="kpsi-link-btn"
                  onClick={() =>
                    setWork((rows) => (rows.length && isWorkEmpty(rows[rows.length - 1]!) ? rows : [...rows, {}]))
                  }
                >
                  + Add a Previous Job
                </button>
              </p>
              {work.map((w, i) => (
                <div className="kpsi-work-row" key={i}>
                  <div className="kpsi-form-grid kpsi-form-grid-work">
                    <div className="kpsi-field kpsi-field-work-pos">
                      <label>Position</label>
                      <input
                        value={w.position ?? ""}
                        onChange={(e) =>
                          setWork((rows) =>
                            rows.map((r, idx) => (idx === i ? { ...r, position: e.target.value } : r))
                          )
                        }
                      />
                    </div>
                    <div className="kpsi-field">
                      <label>Start Date</label>
                      <input
                        value={w.startDate ?? ""}
                        onChange={(e) =>
                          setWork((rows) =>
                            rows.map((r, idx) => (idx === i ? { ...r, startDate: e.target.value } : r))
                          )
                        }
                      />
                    </div>
                    <div className="kpsi-field">
                      <label>End Date</label>
                      <input
                        value={w.endDate ?? ""}
                        onChange={(e) =>
                          setWork((rows) =>
                            rows.map((r, idx) => (idx === i ? { ...r, endDate: e.target.value } : r))
                          )
                        }
                      />
                    </div>
                    <div className="kpsi-field kpsi-field-work-reason">
                      <label>Reason</label>
                      <input
                        value={w.reason ?? ""}
                        onChange={(e) =>
                          setWork((rows) =>
                            rows.map((r, idx) => (idx === i ? { ...r, reason: e.target.value } : r))
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" className="kpsi-btn-secondary" onClick={goNext}>
                Next
              </button>
            </>
          )}

          {tab === 5 && (
            <>
              <div className="kpsi-field">
                <label>Notes</label>
                <textarea
                  value={String(t.notes ?? "")}
                  onChange={(e) => setField("notes", e.target.value)}
                  rows={10}
                />
              </div>
              <button type="submit" className="kpsi-btn-primary">
                Submit
              </button>
            </>
          )}
        </div>
      </form>
    </>
  );
}
